<template>
  <div class="bg-white rounded-lg border border-gray-200 overflow-hidden relative">
    <div class="px-4 py-3 bg-gray-50 border-b border-gray-200">
      <h4 class="text-md font-semibold text-gray-800">
        <i class="fas fa-clock text-blue-500 mr-2"></i>
        Minutes Settings Overview
      </h4>
    </div>
    
    <!-- Loading State -->
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <div class="text-center text-gray-500">
        <i class="fas fa-spinner fa-spin text-2xl mb-2"></i>
        <p class="text-sm">Loading minutes settings data...</p>
      </div>
    </div>
    
    <!-- Error State -->
    <div v-else-if="error" class="flex items-center justify-center py-12">
      <div class="text-center text-red-500">
        <i class="fas fa-exclamation-triangle text-2xl mb-2"></i>
        <p class="text-sm">{{ error }}</p>
        <button 
          @click="$emit('retry')"
          class="mt-2 px-3 py-1 bg-red-500 text-white rounded text-xs hover:bg-red-600"
        >
          Retry
        </button>
      </div>
    </div>
    
    <!-- Data Display -->
    <div v-else-if="data && data.length > 0" class="overflow-x-auto">
      <table class="w-full">
        <thead class="bg-gray-50 border-b border-gray-200">
          <tr>
            <th class="px-4 py-3 text-left text-sm font-semibold text-gray-700">Template</th>
    
            <th class="px-4 py-3 text-center text-sm font-semibold text-gray-700">Per Game</th>
       
          </tr>
        </thead>
        <tbody>
          <tr 
            v-for="(item, index) in data" 
            :key="`minutes-${index}`"
            class="border-b border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer"
            @click="openSeasonDetailsModal(item)"
          >
            <!-- Template Label -->
            <td class="px-4 py-3">
              <div class="flex items-center">
                <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                      :class="getTemplateBadgeClass(item.template_label)">
                  {{ item.template_label }}
                  <span v-if="!doesTemplateMatchExpected(item)" 
                        class="ml-1 text-red-500 font-bold relative"
                        @mouseenter="showTooltip($event, item, index)"
                        @mouseleave="hideTooltip">
                    !
                  </span>
                </span>
              </div>
            </td>
            
     

            
            <!-- Per Game Minutes -->
            <td class="px-4 py-3 text-center">
              <div class="flex items-center justify-center">
                <span class="text-sm font-medium text-gray-900 mr-1">
                  {{ item.per_game_minutes }}
                </span>
                <span class="text-xs text-gray-500">min</span>
              </div>
            </td>
            

            
    
          </tr>
        </tbody>
      </table>
    </div>
    
    <!-- Empty State -->
    <div v-else class="flex items-center justify-center py-12">
      <div class="text-center text-gray-500">
        <i class="fas fa-info-circle text-2xl mb-2"></i>
        <p class="text-sm">No minutes settings data available</p>
      </div>
    </div>

    <!-- Custom Tooltip -->
    <div v-if="tooltip.show" 
         class="absolute z-50 bg-gray-900 text-white text-xs rounded-lg py-2 px-3 shadow-lg max-w-sm"
         :style="{ left: tooltip.x + 'px', top: tooltip.y + 'px' }">
      <div class="whitespace-pre-line">{{ tooltip.content }}</div>
      <!-- Tooltip Arrow -->
      <div class="absolute w-2 h-2 bg-gray-900 transform rotate-45 -bottom-1 left-4"></div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MinutesSettingsTable',
  props: {
    data: {
      type: Array,
      default: () => []
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: null
    }
  },
  emits: ['retry', 'open-season-details'],
  data() {
    return {
      tooltip: {
        show: false,
        content: '',
        x: 0,
        y: 0
      }
    }
  },
  methods: {
    getExpectedSeasonsForTemplate(templateLabel) {
      const expectedSeasons = {
        'Last Season': ['2024-2025'],
        '2 Seasons ago': ['2023-2024'],
        '3 Seasons ago': ['2022-2023'],
        'Last 5 games': ['2025-2026'],
        'Without Last 5': ['2025-2026']
      }
      return expectedSeasons[templateLabel] || []
    },
    
    getExpectedFiltersForTemplate(templateLabel) {
      const expectedFilters = {
        'Last Season': ['allGames'],
        '2 Seasons ago': ['allGames'],
        '3 Seasons ago': ['allGames'],
        'Last 5 games': ['lastFiveGames'],
        'Without Last 5': ['withoutLast5Games']
      }
      return expectedFilters[templateLabel] || []
    },
    
    getValidationDetails(item) {
      const details = {
        isValid: true,
        errors: []
      }
      
      // Validate seasons
      if (!item.seasons || !Array.isArray(item.seasons)) {
        details.isValid = false
        details.errors.push('Missing or invalid seasons data')
        return details
      }
      
      const expectedSeasons = this.getExpectedSeasonsForTemplate(item.template_label)
      if (expectedSeasons.length > 0) {
        // Check if seasons match exactly (same length and same values)
        if (item.seasons.length !== expectedSeasons.length) {
          details.isValid = false
          details.errors.push(`Expected seasons: ${expectedSeasons.join(', ')} | Actual: ${item.seasons.join(', ')}`)
        } else {
          // Sort both arrays and compare seasons
          const itemSeasons = [...item.seasons].sort()
          const expectedSeasonsSorted = [...expectedSeasons].sort()
          
          const seasonsMatch = itemSeasons.every((season, index) => season === expectedSeasonsSorted[index])
          if (!seasonsMatch) {
            details.isValid = false
            details.errors.push(`Expected seasons: ${expectedSeasons.join(', ')} | Actual: ${item.seasons.join(', ')}`)
          }
        }
      }
      
      // Validate additional filter
      const expectedFilters = this.getExpectedFiltersForTemplate(item.template_label)
      if (expectedFilters.length > 0) {
        if (!expectedFilters.includes(item.additional_filter)) {
          details.isValid = false
          details.errors.push(`Expected filter: ${expectedFilters.join(' or ')} | Actual: ${item.additional_filter || 'None'}`)
        }
      }
      
      return details
    },
    
    doesTemplateMatchExpected(item) {
      return this.getValidationDetails(item).isValid
    },
    
    getValidationTooltip(item) {
      const validation = this.getValidationDetails(item)
      if (validation.isValid) {
        return 'Template configuration is correct'
      }
      return validation.errors.join('\n')
    },

    showTooltip(event, item, index) {
      const validation = this.getValidationDetails(item)
      this.tooltip.content = validation.isValid ? 'Template configuration is correct' : validation.errors.join('\n')
      
      // Get the position relative to the component
      const rect = this.$el.getBoundingClientRect()
      const targetRect = event.target.getBoundingClientRect()
      
      // Position tooltip above the alert icon with some offset
      this.tooltip.x = targetRect.left - rect.left - 10
      this.tooltip.y = targetRect.top - rect.top - 40
      
      // Ensure tooltip doesn't go off-screen to the left
      if (this.tooltip.x < 0) {
        this.tooltip.x = 10
      }
      
      // Show the tooltip
      this.tooltip.show = true
    },

    hideTooltip() {
      this.tooltip.show = false
    },
    
    getTemplateBadgeClass(templateLabel) {
      const classes = {
        'Last Season': 'bg-gray-100 text-gray-700',
        '2 Seasons ago': 'bg-gray-100 text-gray-700',
        '3 Seasons ago': 'bg-gray-100 text-gray-700',
        'Last 5 games': 'bg-gray-100 text-gray-700',
        'Rest of Curr season': 'bg-gray-100 text-gray-700'
      }
      return classes[templateLabel] || 'bg-gray-100 text-gray-700'
    },
    
    getFilterBadgeClass(filter) {
      const classes = {
        'allGames': 'bg-gray-100 text-gray-800',
        'lastFiveGames': 'bg-blue-100 text-blue-800',
        'withoutLast5Games': 'bg-purple-100 text-purple-800',
        'restOfSeason': 'bg-purple-100 text-purple-800'
      }
      return classes[filter] || 'bg-gray-100 text-gray-800'
    },
    
    formatFilterLabel(filter) {
      const labels = {
        'allGames': 'All Games',
        'lastFiveGames': 'Last 5 Games',
        'withoutLast5Games': 'Exclude Last 5',
        'restOfSeason': 'Without Last 5 (Legacy)'
      }
      return labels[filter] || filter
    },
    
    openSeasonDetailsModal(item) {
      // Emit event to parent component with the template label and player ID
      this.$emit('open-season-details', {
        templateLabel: item.template_label,
        playerId: item.player_id,
        additionalFilter: item.additional_filter,
        seasons: item.seasons,
        leagues: item.leagues
      })
    }
  }
}
</script>
